import {
  isEnumeratedEnumVFrame,
  isFixedEnumVFrame,
  isFixedFractionVFrame,
  isFixedStringVFrame,
  type PartitionCommandSelection,
  type PartitionCommandSelectionMFrame,
  type SwitchSubPortfolioGroupCommandData,
  type SwitchSubPortfolioGroupCommandDataMFrame,
} from '@festina/unitlink-general-types/unitlink'
import {
  amountUtils,
  type Compare,
  listUtils,
  notEmpty,
  stringUtils,
} from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import {
  useFormikContext,
  useTranslate,
  useTranslateEnum,
} from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  type RowAction,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { CompareValues, Typography } from '@festina-life/ui/structure'
import unionWith from 'lodash-es/unionWith'
import { useMemo, useState } from 'react'

import { EditPartitionCommandSelectionModal } from './EditPartitionCommandSelectionModal'
import { FundCommandSelectionTable } from './FundCommandSelectionTable'
import { updatePartitionCommandSelections } from './switchSubPortfolioGroupUtils'
import type { PartitionCommandSelectionWithCurrentFractions } from './types'

type Props = {
  mFrame: SwitchSubPortfolioGroupCommandDataMFrame | null
  isSinglePartition: boolean
}

const defineColumn =
  createColumnDefiner<Compare<PartitionCommandSelectionWithCurrentFractions>>()

export const PartitionCommandSelectionTable = ({
  mFrame,
  isSinglePartition,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const {
    values,
    setFieldValue,
    status: { shouldFieldsBeDisabled },
  } = useFormikContext<SwitchSubPortfolioGroupCommandData>()

  const [editProfileType, setEditProfileType] =
    useState<PartitionCommandSelection>()
  const onEditPartitionClose = () => setEditProfileType(undefined)

  const deleteProfileType = async (
    partition: PartitionCommandSelectionWithCurrentFractions,
  ) => {
    const existingPartitions = values.partitionCommandSelections ?? []
    const updatedSelections = existingPartitions.filter(
      (selection) => selection?.profileType !== partition.profileType,
    )
    await setFieldValue('partitionCommandSelections', updatedSelections)
  }

  const onEditPartitionSubmit = async (
    updatedPartition: PartitionCommandSelection,
  ) => {
    const updatedSelections = updatePartitionCommandSelections(
      // only pass the necessary fields, otherwise the fund selections will be lost
      {
        _type: 'PartitionCommandSelection',
        profileType: updatedPartition.profileType,
        fraction: updatedPartition.fraction,
      },
      values?.partitionCommandSelections?.filter(notEmpty) ?? [],
    )

    await setFieldValue('partitionCommandSelections', updatedSelections)
    setEditProfileType(undefined)
  }

  const existingPartitionMFrames =
    mFrame?.updatePartitionCommandSelections?.filter(notEmpty) ?? []

  const addedPartitionMFrames =
    mFrame?.addPartitionCommandSelections?.filter(notEmpty) ?? []

  const getProfileType = (partitionMFrame: PartitionCommandSelectionMFrame) => {
    const profileTypeVFrame = partitionMFrame?.profileType

    // In multipartition case, profileType is fixed
    if (isFixedEnumVFrame(profileTypeVFrame)) {
      return profileTypeVFrame.value
    }

    return (
      // In single partition case, take the profileType from the single partitionCommandSelections or use the defaultValue from the frame
      values.partitionCommandSelections?.[0]?.profileType ??
      (isEnumeratedEnumVFrame(profileTypeVFrame)
        ? profileTypeVFrame.defaultValue
        : undefined)
    )
  }

  const existingPartitionCommandSelections: PartitionCommandSelectionWithCurrentFractions[] =
    existingPartitionMFrames.map((partition) => {
      const profileType = getProfileType(partition)
      const fundCommandSelections = values.partitionCommandSelections?.find(
        (newSelection) => newSelection?.profileType === profileType,
      )?.fundCommandSelections

      return {
        _type: 'PartitionCommandSelection',
        profileType,
        currentFraction: partition.currentFraction,
        currentCashValueFraction: partition.currentCashValueFraction,
        fundCommandSelections,
      }
    }) ?? []

  const updatedPartitionCommandSelections: PartitionCommandSelectionWithCurrentFractions[] =
    values.partitionCommandSelections
      ?.filter(notEmpty)
      .map((updatedPartition) => {
        const existingPartition = existingPartitionCommandSelections.find(
          (existing) => existing.profileType === updatedPartition.profileType,
        )

        const currentFraction = stringUtils.strictIncludes(
          values?.switchCommandType,
          ['paymentAndSavingsDistribution', 'paymentDistribution'],
        )
          ? (updatedPartition.fraction ?? existingPartition?.currentFraction)
          : existingPartition?.currentFraction

        const currentCashValueFraction = stringUtils.strictIncludes(
          values?.switchCommandType,
          ['paymentAndSavingsDistribution', 'savingsDistribution'],
        )
          ? (updatedPartition.fraction ??
            existingPartition?.currentCashValueFraction)
          : existingPartition?.currentCashValueFraction

        return {
          _type: 'PartitionCommandSelection',
          profileType: updatedPartition.profileType,
          currentFraction,
          currentCashValueFraction,
          fundCommandSelections: updatedPartition.fundCommandSelections,
        }
      }) ?? []

  const currentPartitions = unionWith(
    updatedPartitionCommandSelections,
    existingPartitionCommandSelections,
    (a, b) => a.profileType === b.profileType,
  )

  const partitions = listUtils.getListOfPairs({
    currentList: currentPartitions,
    prevList: existingPartitionCommandSelections,
    keyFunc: ({ profileType }) => profileType,
  })

  const canDelete = (
    partition: PartitionCommandSelectionWithCurrentFractions,
  ) => {
    const isExisting = existingPartitionCommandSelections.some(
      (existing) => existing.profileType === partition.profileType,
    )
    return !shouldFieldsBeDisabled && !isExisting
  }

  const canEdit = (
    partition: PartitionCommandSelectionWithCurrentFractions,
  ) => {
    const fundFrame = existingPartitionMFrames.find(
      (mFrame) =>
        isFixedStringVFrame(mFrame.profileType) &&
        mFrame.profileType.value === partition.profileType,
    )
    return (
      !shouldFieldsBeDisabled && !isFixedFractionVFrame(fundFrame?.fraction)
    )
  }

  // For some reason the partitions available for adding are given as a list of frames with multiple entries each, so we have to flatten it
  const allAvailableProfileIds = [
    ...new Set(
      mFrame?.addPartitionCommandSelections
        ?.map((frame) =>
          isFixedEnumVFrame(frame?.profileType)
            ? frame.profileType.value
            : undefined,
        )
        .filter(notEmpty) ?? [],
    ),
  ]

  const totalRow: Compare<PartitionCommandSelectionWithCurrentFractions> =
    useMemo(
      () =>
        partitions.reduce<
          Compare<PartitionCommandSelectionWithCurrentFractions>
        >(
          (acc, curr) => ({
            current: {
              _type: 'PartitionCommandSelection',
              profileType: translate('total'),
              currentFraction: amountUtils.addMaybe(
                acc.current?.currentFraction,
                curr.current?.currentFraction,
              ),
              currentCashValueFraction: amountUtils.addMaybe(
                acc.current?.currentCashValueFraction,
                curr.current?.currentCashValueFraction,
              ),
            },
            compare: {
              _type: 'PartitionCommandSelection',
              profileType: translate('total'),
              currentFraction: amountUtils.addMaybe(
                acc.compare?.currentFraction,
                curr.compare?.currentFraction,
              ),
              currentCashValueFraction: amountUtils.addMaybe(
                acc.compare?.currentCashValueFraction,
                curr.compare?.currentCashValueFraction,
              ),
            },
          }),
          {
            current: {
              _type: 'PartitionCommandSelection',
            },
            compare: {
              _type: 'PartitionCommandSelection',
            },
          },
        ),
      [partitions, translate],
    )

  // Remove already added partitions so you can't add them again
  const remainingAvailablePartitions = allAvailableProfileIds.filter(
    (profileId) =>
      !currentPartitions.some(
        (selection) => selection.profileType === profileId,
      ),
  )

  const rowActions = ({
    compare,
    current,
  }: Compare<PartitionCommandSelectionWithCurrentFractions>): RowAction[] => {
    const partition = current ?? compare

    return [
      {
        key: 'edit',
        title: translate('edit'),
        iconName: 'Pencil',
        onClick: canEdit(partition)
          ? () => setEditProfileType(partition)
          : undefined,
      },
      {
        key: 'delete',
        title: translate('delete'),
        iconName: 'Trash',
        onClick: canDelete(partition)
          ? () => deleteProfileType(partition)
          : undefined,
      },
    ]
  }

  const cols = useMemo(
    () => ({
      profileType: defineColumn({
        heading: translate('partitionCommandSelectionTable.profileType'),
        keyPath: undefined,
        formatter: ({ compare, current }) => (
          <CompareValues
            keyPath="profileType"
            prev={compare}
            current={current}
            formatter={(profileType) =>
              translateEnum('unitLink.enum.ProfileType', profileType)
            }
            isDiffing
          />
        ),
        sortValue: (x) =>
          translateEnum('unitLink.enum.ProfileType', x.current?.profileType),
      }),
      currentFraction: defineColumn({
        heading: translate('partitionCommandSelectionTable.currentFraction'),
        keyPath: undefined,
        formatter: ({ compare, current }) => (
          <CompareValues
            keyPath="currentFraction"
            prev={compare}
            current={current}
            formatter={amountUtils.formatPercent}
            isDiffing
          />
        ),
        sortValue: (x) => x.current?.currentFraction,
      }),
      currentCashValueFraction: defineColumn({
        heading: translate(
          'partitionCommandSelectionTable.currentCashValueFraction',
        ),
        keyPath: undefined,
        formatter: ({ compare, current }) => (
          <CompareValues
            keyPath="currentCashValueFraction"
            prev={compare}
            current={current}
            formatter={amountUtils.formatPercent}
            isDiffing
          />
        ),
        sortValue: (x) => x.current?.currentCashValueFraction,
      }),
    }),
    [translate, translateEnum],
  )

  const isPartitionTableVisible =
    !!values.switchCommandType && !isSinglePartition

  return (
    <>
      {isPartitionTableVisible && (
        <>
          <div className="flex items-end mb-2">
            <Typography intensity="primary" weight="bold" className="grow">
              {translate('partitionCommandSelectionTable.partitionsTableTitle')}
            </Typography>
            <Button
              className="self-end"
              variant="secondary"
              disabled={!remainingAvailablePartitions.length}
              onClick={() =>
                setEditProfileType({ _type: 'PartitionCommandSelection' })
              }
            >
              {translate('partitionCommandSelectionTable.addNewPartition')}
            </Button>
          </div>
          <SortableTable
            columns={cols}
            data={partitions}
            footerData={[totalRow]}
            rowActions={rowActions}
          />
        </>
      )}
      {currentPartitions.map((partition) => (
        <FundCommandSelectionTable
          key={partition?.profileType}
          mFrame={
            isSinglePartition
              ? existingPartitionMFrames[0] // there is only one PartitionCommandSelectionMFrame in single partition case
              : existingPartitionMFrames
                  .concat(addedPartitionMFrames)
                  .find(
                    (frame) =>
                      isFixedEnumVFrame(frame?.profileType) &&
                      frame.profileType.value === partition.profileType,
                  )
          }
          partitionCommandSelection={partition}
          title={translate('fundCommandSelectionTable.fundsTableTitle', {
            profileType:
              translateEnum(
                'unitLink.enum.ProfileType',
                partition.profileType,
              ) ?? '-',
          })}
        />
      ))}
      {editProfileType && (
        <EditPartitionCommandSelectionModal
          partitionCommandSelection={editProfileType}
          existingProfileTypes={currentPartitions
            .map((selection) => selection.profileType)
            .filter(notEmpty)}
          onClose={onEditPartitionClose}
          onSubmit={onEditPartitionSubmit}
          mFrame={mFrame}
        />
      )}
    </>
  )
}
